import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import PageHeader, {ActionButton} from "../../components/layout/page-header";
import {useDispatch, useSelector} from "react-redux";
import AddShipping from "../shipping/add-shipping";
import ActionsMenu from "../../components/common/table/actions-menu";
import {
    CONTAINERS,
    DOCUMENTS,
    EVENTS,
    LOGISTICS,
    SHIPPING,
    SHIPPING_DETAIL,
    SHIPPING_OVERVIEW,
    SHIPPINGS
} from "../endpoints";
import ShippingIcon from "../../resources/icons/ShippingIcon";
import {getFormattedDate} from "../../utils/timeUtils";
import {getShippingDynamic} from "../../api/shipping";
import {getPathParam, getWebPathParam} from "../../utils/converter";
import CustomerCell from "../../components/common/table/customer-cell";
import {getPermission, permissionGroup, permissionType, sp} from "../../constants/permissions";
import {useTranslation} from "react-i18next";
import OptionalLink from "../../components/common/table/optional-link";
import RemovePopup from "../../components/common/popup/remove-popup";
import {AIR, COMPANY_EXPORTER, COMPANY_IMPORTER, LAND, SEA} from "../../constants/config";
import {hasSubscriptionPermission} from "../../components/partials/restricted/subscription-wrapper";
import {subscriptionUsageLimits} from "../../reducers/subscriptionReducer";
import {PlusIcon, TruckIcon} from "@heroicons/react/outline";
import AnimatedPing from "../../components/common/progress/animated-ping";
import BasePermissionWrapper, {hasPermissionFor} from "../../components/partials/restricted/base-permission-wrapper";
import {StatusBarIcon} from "../../components/common/alert/status-bar-icon";
import AirIcon from "../../resources/icons/AirIcon";
import {generateParamsFormLocation} from "../../constants/dynamic-filter";
import {COMPANY, SHIPPING_IMPORTER} from "../../api/endpoints";
import {DataTable} from "../../components/table/data-table";
import DynamicFilterMenu from "../../components/common/table/dynamic-filter-menu";

const PAGE_LIMIT = 40;

const DataTableDemo = () => {
    const {loggedCompany, companyType, isExporter} = useSelector((state) => state.user);
    const {permissions, subscriptions} = useSelector((s) => s.userCompany);
    const {t} = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const companyId = loggedCompany?.id;

    const [isLoading, setIsLoading] = useState(true);
    const [shippingList, setShippingList] = useState([]);
    const [shippingListRows, setShippingListRows] = useState(0);
    const [limit] = useState(PAGE_LIMIT);
    const [offset, setOffset] = useState(() => {
        const pageNumber = new URLSearchParams(window.location.search).get("page");
        if (pageNumber) {
            return parseInt(pageNumber * PAGE_LIMIT - PAGE_LIMIT, 10);
        }
        return 0;
    });

    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenRemove, setOpenRemove] = useState(false);
    const [removeDetail, setRemoveDetail] = useState({});
    const fetchedOnceRef = useRef(false);
    const [refreshIndex, setRefreshIndex] = useState(0);
    const [dynamicFilters, setDynamicFilters] = useState(false);
    const [defaultParams, setDefaultParams] = useState(false);
    const [activeDynamicFilter, setDynamicDateFilter] = useState(generateParamsFormLocation(location.search));
    const [defaultParamsUsed, setDefaultParamsUsed] = useState(false);
    const [lastCompanyType, setLastCompanyType] = useState(companyType);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [tableMode] = useState('infinite');
    const [sortParams, setSortParams] = useState({sort_name: null, sort_type: null});

    useEffect(() => {
        if (companyType !== lastCompanyType) {
            fetchedOnceRef.current = false;
            setIsLoading(true);
            setDynamicDateFilter([]);
            setDefaultParamsUsed(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyType]);

    useEffect(() => {
        fetchedOnceRef.current = false;
        setOffset(0);
        setShippingList([]);
        setIsFetchingMore(false);
        setIsLoading(true);
    }, [tableMode]);

    const tablePermissions = useMemo(() => ({
        onRemove: (state, detail) => {
            setOpenRemove(state);
            setRemoveDetail(detail);
        },
        isSubscriptionActive: hasSubscriptionPermission(subscriptions, sp.SHIPPING_INTERNAL_PACKAGE),
        isLinked: hasPermissionFor(permissions, permissionGroup.SHIPPING, permissionType.EDIT),
        canView: hasPermissionFor(permissions, permissionGroup.SHIPPING, permissionType.VIEW),
        canDelete: hasPermissionFor(permissions, permissionGroup.SHIPPING, permissionType.DELETE),
        isLinkedContainer: hasPermissionFor(permissions, permissionGroup.CONTAINER, permissionType.LIST),
        isLinkedDocument: hasPermissionFor(permissions, permissionGroup.DOCUMENT, permissionType.LIST),
        isLinkedEvent: hasPermissionFor(permissions, permissionGroup.EVENT, permissionType.LIST),
    }), [permissions, subscriptions]);

    const canEditShipping = useCallback((element) => {
        if (!element) {
            return false;
        }
        return (companyType === element.creator_type) ||
            (isExporter && element.creator_type === COMPANY_IMPORTER && element.can_edit) ||
            (!isExporter && element.creator_type === COMPANY_EXPORTER && element.can_edit);
    }, [companyType, isExporter]);

    const columns = useMemo(() => {
        return [
            {
                id: "business_name",
                accessorFn: (row) => row?.customer?.business_name ?? "",
                header: () => (isExporter || isExporter === null ? t("app.common.customer") : t("app.common.supplier")),
                title: isExporter || isExporter === null ? t("app.common.customer") : t("app.common.supplier"),
                size: 280,
                cell: ({row}) => {
                    const element = row.original;
                    const canEdit = canEditShipping(element);
                    const linkTo = getWebPathParam([LOGISTICS, SHIPPING, element.id, SHIPPING_DETAIL]);
                    return (
                        <div className="flex items-center gap-3">
                            {element.tracking_state && (
                                <AnimatedPing color={element.tracking_state.error_color}
                                              extraClass="sm:mt-4 sm:-ml-4 lg:mt-2 lg:-ml-3"/>
                            )}
                            <OptionalLink
                                isLinked={tablePermissions.isSubscriptionActive && tablePermissions.isLinked && canEdit}
                                to={linkTo}
                                className="truncate hover:text-gray-600"
                            >
                                <CustomerCell customer={element.customer}
                                              extra={!isExporter ? (element?.customer_note ?? false) : false}/>
                            </OptionalLink>
                        </div>
                    );
                },
            },
            {
                id: "destination",
                accessorFn: (row) => row?.destination?.name ?? "",
                header: () => t("app.shippings.destination"),
                title: t("app.shippings.destination"),
                size: 240,
                cell: ({row}) => {
                    const element = row.original;
                    return (
                        <div className="flex items-center gap-3">
                            <ShippingTypeIcon type={element?.shipping_type?.code}/>
                            <div>
                                <div className="text-sm text-gray-900 capitalize">
                                    {element?.destination?.name ?? "-"}
                                </div>
                                <div className="text-xs text-gray-500 capitalize">
                                    {element?.destination?.country ?? "-"}
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: "tracking",
                accessorKey: "tracking",
                header: () => t("app.shippings.tracking"),
                title: t("app.shippings.tracking"),
                size: 160,
                cell: ({getValue}) => getValue() || t("app.container.container_not_defined"),
            },
            {
                id: "departure_date",
                accessorKey: "departure_date",
                header: () => t("app.shippings.dep_date"),
                title: t("app.shippings.dep_date"),
                size: 160,
                cell: ({getValue}) => getFormattedDate(getValue()),
            },
            {
                id: "arrival_date",
                accessorKey: "arrival_date",
                header: () => t("app.shippings.arr_date"),
                title: t("app.shippings.arr_date"),
                size: 160,
                cell: ({getValue}) => getFormattedDate(getValue()),
            },
            {
                id: "status",
                accessorKey: "status",
                header: () => t("app.shippings.status"),
                title: t("app.shippings.status"),
                size: 140,
                cell: ({row}) => {
                    const element = row.original;
                    const statusValue = isExporter ? element.status : (element.shipping_closed_importer ? "completed" : "in_progress");
                    return (
                        <div className="flex">
                            <StatusBarIcon status={statusValue}/>
                        </div>
                    );
                },
            },
            {
                id: "actions",
                header: "",
                title: "",
                size: 50,
                enableSorting: false,
                className: "text-right",
                cell: ({row}) => {
                    const element = row.original;
                    const canEdit = canEditShipping(element);
                    const removeDetail = loggedCompany ? {
                        title: t("app.shippings.remove_title"),
                        message: t("app.shippings.remove_description"),
                        endpoint: getPathParam([COMPANY, loggedCompany.id, isExporter ? SHIPPING : SHIPPING_IMPORTER, element.id]),
                        redirect: getWebPathParam([LOGISTICS, SHIPPINGS]),
                    } : null;

                    return (
                        <ActionsMenu>
                            {tablePermissions.isSubscriptionActive && tablePermissions.isLinked && canEdit ? (
                                <Link to={getWebPathParam([LOGISTICS + SHIPPING, element.id, SHIPPING_DETAIL])}>
                                    {t("app.common.edit")}
                                </Link>
                            ) : tablePermissions.isSubscriptionActive && tablePermissions.isLinkedContainer && canEdit ? (
                                <Link to={getWebPathParam([LOGISTICS + SHIPPING, element.id, CONTAINERS])}>
                                    {t("app.common.edit")}
                                </Link>
                            ) : tablePermissions.isSubscriptionActive && tablePermissions.isLinkedDocument && canEdit ? (
                                <Link to={getWebPathParam([LOGISTICS + SHIPPING, element.id, DOCUMENTS])}>
                                    {t("app.common.edit")}
                                </Link>
                            ) : tablePermissions.isSubscriptionActive && tablePermissions.isLinkedEvent && canEdit ? (
                                <Link to={getWebPathParam([LOGISTICS + SHIPPING, element.id, EVENTS])}>
                                    {t("app.common.edit")}
                                </Link>
                            ) : null}

                            {tablePermissions.canView && (
                                <Link to={getWebPathParam([LOGISTICS + SHIPPING, element.id, SHIPPING_OVERVIEW])}>
                                    {t("app.common.overview")}
                                </Link>
                            )}

                            {tablePermissions.isSubscriptionActive && tablePermissions.canDelete && removeDetail &&
                                companyType === element.creator_type &&
                                ((isExporter && element.shipping_closed === false) || (!isExporter && element.shipping_closed_importer === false)) && (
                                    <button
                                        type="button"
                                        className="text-left"
                                        onClick={() => tablePermissions.onRemove(true, removeDetail)}
                                    >
                                        {t("app.common.remove")}
                                    </button>
                                )
                            }
                        </ActionsMenu>
                    );
                },
            },
        ];
    }, [canEditShipping, companyType, isExporter, loggedCompany, tablePermissions, t]);

    const buildParams = useCallback((additional = {}) => {
        const params = {
            limit,
            query,
            remove_default: !defaultParamsUsed,
            company_type: companyType,
            ...additional,
        };

        if (sortParams.sort_name && sortParams.sort_type) {
            params.sort_name = sortParams.sort_name;
            params.sort_type = sortParams.sort_type;
        }

        return params;
    }, [companyType, defaultParamsUsed, limit, query, sortParams]);

    useEffect(() => {
        if (!companyId) {
            setShippingList([]);
            setShippingListRows(0);
            setIsLoading(false);
            return;
        }

        if (tableMode === 'infinite' && offset > 0) {
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        async function fetchInitial() {
            const shouldShowLoading = tableMode === 'pagination' || !fetchedOnceRef.current;
            if (shouldShowLoading) {
                setIsLoading(true);
            }

            try {
                const response = await getShippingDynamic({
                    company_id: companyId,
                    params: buildParams({offset: tableMode === 'pagination' ? offset : 0}),
                    data: activeDynamicFilter,
                    signal,
                });

                if (response?.filters) {
                    setDynamicFilters(response.filters);
                }

                if (
                    response?.default_filter &&
                    !defaultParamsUsed &&
                    generateParamsFormLocation(window.location.search).length === 0
                ) {
                    setDefaultParams(`?${response.default_filter}`);
                }

                setShippingListRows(response?.total_rows ?? 0);
                setShippingList(response?.shippings ?? []);
                setLastCompanyType(companyType);
                fetchedOnceRef.current = true;
            } catch (error) {
                if (!signal.aborted) {
                    console.error("Errore durante il fetch:", error);
                    setShippingList([]);
                    setShippingListRows(0);
                }
            } finally {
                if (shouldShowLoading) {
                    setIsLoading(false);
                }
                setIsFetchingMore(false);
            }
        }

        fetchInitial();

        return () => controller.abort();
    }, [activeDynamicFilter, buildParams, companyId, companyType, defaultParamsUsed, offset, tableMode, refreshIndex]);

    useEffect(() => {
        if (!companyId || tableMode !== 'infinite' || offset === 0) {
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        async function fetchMore() {
            try {
                setIsFetchingMore(true);
                const response = await getShippingDynamic({
                    company_id: companyId,
                    params: buildParams({offset}),
                    data: activeDynamicFilter,
                    signal,
                });

                if (typeof response?.total_rows === "number") {
                    setShippingListRows(response.total_rows);
                }

                setShippingList((prev) => {
                    const existingIds = new Set(prev.map((item) => item.id));
                    const nextItems = (response?.shippings ?? []).filter((item) => !existingIds.has(item.id));
                    return [...prev, ...nextItems];
                });
            } catch (error) {
                if (!signal.aborted) {
                    console.error("Errore durante il fetch:", error);
                }
            } finally {
                if (!signal.aborted) {
                    setIsFetchingMore(false);
                }
            }
        }

        fetchMore();

        return () => controller.abort();
    }, [activeDynamicFilter, buildParams, companyId, offset, tableMode]);

    const handleSearch = useCallback((value) => {
        fetchedOnceRef.current = false;
        setOffset(0);
        setShippingList([]);
        setQuery(value);
        setIsLoading(true);
    }, []);

    const handleSorting = useCallback((sorting) => {
        let shouldReset = false;

        setSortParams((prev) => {
            const nextSortName = sorting?.sortKey !== undefined && sorting?.sortKey !== null
                ? String(sorting.sortKey)
                : null;
            const nextSortType = sorting?.sortDir !== undefined && sorting?.sortDir !== null
                ? String(sorting.sortDir)
                : null;
            const same =
                prev.sort_name === nextSortName
                && prev.sort_type === nextSortType;

            if (same) {
                return prev;
            }

            shouldReset = true;

            if (!sorting) {
                return {sort_name: null, sort_type: null};
            }

            return {sort_name: nextSortName, sort_type: nextSortType};
        });

        if (!shouldReset) {
            return;
        }

        fetchedOnceRef.current = false;
        setOffset(0);
        setShippingList([]);
        setIsLoading(true);
    }, []);

    const handleDynamicFilter = useCallback((filters) => {
        fetchedOnceRef.current = false;
        setDynamicDateFilter(filters);
        setOffset(0);
        setShippingList([]);
        setDefaultParamsUsed(true);
        setDefaultParams(false);
        setIsLoading(true);
    }, []);

    const handleFetchMore = useCallback((nextOffset) => {
        if (tableMode !== 'infinite') {
            return;
        }
        if (isFetchingMore) {
            return;
        }
        if (shippingList.length >= shippingListRows) {
            return;
        }
        setIsFetchingMore(true);
        setOffset(nextOffset);
    }, [tableMode, isFetchingMore, shippingList.length, shippingListRows]);

    const handlePaginationChange = useCallback((nextOffset) => {
        if (tableMode !== 'pagination') {
            return;
        }
        fetchedOnceRef.current = false;
        setShippingList([]);
        setIsFetchingMore(false);
        setIsLoading(true);
        setOffset(nextOffset);
    }, [tableMode]);

    const isTableLoading = tableMode === 'pagination' ? isLoading : (isLoading && offset === 0);

    return (
        <BasePermissionWrapper requiredPermissions={[getPermission(permissionGroup.SHIPPING, permissionType.LIST)]} renderBaseOnFail={true}>
            <div className="pt-4 bg-white overflow-hidden relative ">

                <div className="mx-auto max-w-full px-2  pb-2">
                    <PageHeader canSwitch title={t("app.shippings.shippings")}>
                        <BasePermissionWrapper requiredPermissions={[getPermission(permissionGroup.SHIPPING, permissionType.CREATE)]}>
                            <ActionButton
                                icon={<PlusIcon className="h-4 w-4"/>}
                                onClick={() => setIsOpen(true)}
                                text={t("app.shippings.new_shipping")}
                            />
                        </BasePermissionWrapper>
                    </PageHeader>
                </div>

                <div className="mx-auto max-w-full ">
                    <div className="shadow-sm">
                        <DataTable
                            listType="shippingDemo"
                            leftPinning={["business_name"]}
                            rightPinning={["actions"]}
                            data={shippingList}
                            columns={columns}
                            total={shippingListRows}
                            offset={offset}
                            limit={limit}
                            isLoading={isTableLoading}
                            isFetchingMore={isFetchingMore}
                            onFetchingMore={handleFetchMore}
                            onChangeLimitOffset={handlePaginationChange}
                            onSearch={handleSearch}
                            onSorting={handleSorting}
                            isActiveColumn
                            fixedHeight={"h-[calc(100svh-140px)]"}
                            fixedHeightFilter={"h-[calc(100svh-140px)]"}
                            isFullWitdh={true}
                            activePagination={tableMode === 'pagination'}
                            paginationMode={'infinite'}
                            activeDynamicFilter={true}
                            title={t("app.shippings.shippings")}
                            totalRows={shippingListRows}
                        >
                            <DynamicFilterMenu
                                filters={dynamicFilters || []}
                                defaultParams={defaultParams}
                                lastCompanyType={lastCompanyType}
                                isLoading={isTableLoading && offset === 0}
                                filterType="shippings"
                                onChange={handleDynamicFilter}
                            />
                        </DataTable>
                    </div>
                </div>

                <BasePermissionWrapper requiredPermissions={[getPermission(permissionGroup.SHIPPING, permissionType.DELETE)]}>
                    {isOpenRemove && (
                        <RemovePopup
                            isOpen={isOpenRemove}
                            setIsOpen={setOpenRemove}
                            detail={removeDetail}
                            onRemoveConfirmed={() => {
                                fetchedOnceRef.current = false;
                                setRefreshIndex((index) => index + 1);
                                if (companyId) {
                                    dispatch(subscriptionUsageLimits(companyId));
                                }
                            }}
                        />
                    )}
                </BasePermissionWrapper>

                <BasePermissionWrapper requiredPermissions={[getPermission(permissionGroup.SHIPPING, permissionType.CREATE)]}>
                    {isOpen && <AddShipping isOpen={isOpen} setIsOpen={setIsOpen}/>}
                </BasePermissionWrapper>
            </div>
        </BasePermissionWrapper>
    );
};

function ShippingTypeIcon({type}) {
    if (type === LAND) {
        return <TruckIcon className="h-5 w-5 text-blue-1000"/>;
    }
    if (type === SEA) {
        return <ShippingIcon className="h-5 w-5 text-blue-1000"/>;
    }
    if (type === AIR) {
        return <AirIcon className="h-5 w-5 text-blue-1000"/>;
    }
    return <div className="h-5 w-5"/>;
}

export default DataTableDemo;
